import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import {
  fetchReportingData,
  ReportingApiError,
  type ReportingResponse,
  type ReportingScope,
} from "./reportingClient"
import type { Frequency } from "./frequency"

/**
 * Retrieve auth bearer token from localStorage.
 */
export function getAuthToken(): string {
  try {
    return localStorage.getItem("bearer_token") ?? ""
  } catch {
    return ""
  }
}

/**
 * Format errors returned by reporting API or network failures into user-friendly strings.
 */
export function formatReportingError(err: unknown): string | null {
  if (!err) return null
  if (err instanceof ReportingApiError) {
    switch (err.status) {
      case 400:
        return err.message
      case 403:
        return "You don't have access to this reporting data."
      default:
        return "Something went wrong. Please try again later."
    }
  }
  return "Unable to load reporting data. Check your connection and try again."
}

export interface UseReportingDataOptions {
  scope: ReportingScope
  id: string
  start: string
  stop: string
  frequency: Frequency
  fields: string[]
  enabled?: boolean
}

/**
 * Custom hook fetching reporting data from Timeseries REST API with caching via React Query.
 *
 * Keeps data fresh for 5 minutes (`staleTime: 5 * 60 * 1000`) and cached in memory for 10 minutes (`gcTime: 10 * 60 * 1000`),
 * preventing duplicate network requests when switching between section tabs or navigating back/forward.
 */
export function useReportingData({
  scope,
  id,
  start,
  stop,
  frequency,
  fields,
  enabled = true,
}: UseReportingDataOptions) {
  const startISO = useMemo(
    () => (start ? new Date(start + "T00:00:00").toISOString() : ""),
    [start],
  )
  const stopISO = useMemo(
    () => (stop ? new Date(stop + "T23:59:59").toISOString() : ""),
    [stop],
  )
  const tz = useMemo(() => IANATimeZone(), [])

  return useQuery<ReportingResponse, Error>({
    queryKey: ["reportingData", scope, id, start, stop, frequency],
    queryFn: async ({ signal }) => {
      const token = getAuthToken()
      return fetchReportingData(
        scope,
        id,
        startISO,
        stopISO,
        frequency,
        fields,
        token,
        tz,
        undefined,
        signal,
      )
    },
    enabled: Boolean(enabled && id && fields.length > 0 && start && stop),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}
